import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  ViewButton,
  useToast,
} from '@/components/ui';
import {
  blockedActionReason,
  isCandidateRestricted,
} from '@/features/applications/accountStanding';
import {
  BlockedAction,
  CandidateRestrictionBadge,
} from '@/features/applications/CandidateRestriction';
import { CloseDialog } from '@/features/interviews/CloseDialog';
import { InterviewCalendar } from '@/features/interviews/InterviewCalendar';
import {
  InterviewStandingBadge,
  InterviewStandingNote,
} from '@/features/interviews/InterviewStanding';
import { useRescheduleAnswer } from '@/features/interviews/useRescheduleAnswer';
import { RescheduleRequestPanel } from '@/features/interviews/RescheduleRequestPanel';
import {
  canRecordOutcome,
  hasOpenRequest,
  interviewStanding,
} from '@/features/interviews/reschedule';
import { ScheduleDialog } from '@/features/interviews/ScheduleDialog';
import {
  INTERVIEW_LABELS,
  INTERVIEW_TONES,
  MODE_LABELS,
  interviewWhen,
  whereToGo,
} from '@/features/interviews/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

// The meeting link is an external anchor, not a route, so it borrows the
// tinted main-action button's look (`tintBrand` `sm`).
const JOIN_CALL_CLASS =
  'border border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 ' +
  'rounded-input inline-flex min-h-8 max-w-full items-center ' +
  'justify-center gap-2 px-3 py-1 text-sm font-medium transition';

const TABS = [
  { key: '', label: 'All' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'CANCELLED', label: 'Cancelled' },
  { key: 'NO_SHOW', label: 'Missed' },
];

const PAGE_SIZE = 20;

const VIEWS = [
  { key: 'list', label: 'List' },
  { key: 'calendar', label: 'Calendar' },
];

/**
 * `calendarOnly` is the recruitment dashboard's "Interview calendar": the
 * calendar with no List / Calendar switch. The Interviews page keeps the switch.
 */
export function CompanyInterviewsPage({ calendarOnly = false }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('SCHEDULED');
  const [page, setPage] = useState(1);
  const [moving, setMoving] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [closing, setClosing] = useState(null);
  const [selected, setSelected] = useState(null);
  const answer = useRescheduleAnswer();
  // In the URL, so a refresh or a shared link opens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = calendarOnly || searchParams.get('view') === 'calendar' ? 'calendar' : 'list';

  function setView(next) {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'calendar') params.set('view', 'calendar');
        else params.delete('view');
        return params;
      },
      { replace: true },
    );
  }

  const params = { page, page_size: PAGE_SIZE, ...(status ? { statuses: status } : {}) };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-interviews', params],
    queryFn: () => companyApi.listInterviews(params),
    enabled: view === 'list',
  });

  const { data: counts } = useQuery({
    queryKey: ['interview-counts'],
    queryFn: companyApi.getInterviewCounts,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-interviews'] });
    void queryClient.invalidateQueries({ queryKey: ['interview-counts'] });
    // Cancelling a round steps its application back a stage.
    void queryClient.invalidateQueries({ queryKey: ['company-application'] });
    void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
  }

  const cancel = useMutation({
    mutationFn: ({ id, reason: text }) => companyApi.cancelInterview(id, text),
    onSuccess: () => {
      toast.success('Interview cancelled');
      invalidate();
      setCancelling(null);
      setReason('');
    },
    onError: (cancelError) => toast.error(errorMessage(cancelError, 'Could not cancel')),
  });

  const tabs = TABS.map((tab) => ({
    ...tab,
    count: counts && tab.key ? (counts[tab.key] ?? 0) : undefined,
  }));

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className={cn('flex flex-col', view === 'list' && 'portrait:min-h-[calc(100dvh-6rem)]')}>
      {calendarOnly ? (
        <PageHeader
          title="Interview calendar"
          description="Every round on one calendar. Select one to see it or act on it."
        />
      ) : (
        <PageHeader
          title="Interviews"
          description="Book from an application, then run the round from here."
          action={
            <SegmentedTabs
              className="mb-0"
              label="Interview view"
              tabs={VIEWS}
              value={view}
              onChange={setView}
            />
          }
        />
      )}

      {view === 'calendar' ? (
        <Card className="overflow-hidden p-0">
          <InterviewCalendar onSelect={setSelected} />
        </Card>
      ) : (
        <ListCard
          fill
          className="flex-1"
          toolbarStart={
            <SegmentedTabs
              variant="plain"
              label="Interview status"
              tabs={tabs}
              value={status}
              onChange={(key) => {
                setStatus(key);
                setPage(1);
              }}
            />
          }
          state={
            isError ? (
              <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
            ) : isPending ? (
              <LoadingState label="Loading the schedule…" />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Icon name="calendar" size={22} />}
                title="Nothing scheduled"
                description="Shortlist an applicant, then book an interview from their application."
                action={
                  <LinkButton to={paths.company.applications} variant="secondary" size="sm">
                    Review applications
                  </LinkButton>
                }
              />
            ) : null
          }
          footer={
            meta && (
              <Pagination
                className=""
                page={meta.page}
                pageSize={PAGE_SIZE}
                totalPages={meta.total_pages}
                totalItems={meta.total_items}
                onPageChange={setPage}
              />
            )
          }
        >
          <BoxList>
            {rows.map((interview) => {
              const name = interview.candidate.full_name;
              // Booked rounds stay booked while the candidate's account is
              // suspended, but nothing about them can change.
              const blocked = blockedActionReason(interview);
              return (
                <BoxRow
                  key={interview.id}
                  muted={isCandidateRestricted(interview)}
                  accent={
                    blocked
                      ? 'danger'
                      : ['requested', 'declined'].includes(interviewStanding(interview).key)
                        ? 'warning'
                        : (INTERVIEW_TONES[interview.status] ?? 'neutral')
                  }
                  leading={<BoxAvatar name={name} round />}
                  title={name}
                  to={paths.company.applicationDetail(interview.application_id)}
                  badges={
                    <>
                      <Badge dot tone={INTERVIEW_TONES[interview.status] ?? 'neutral'}>
                        {INTERVIEW_LABELS[interview.status] ?? interview.status}
                      </Badge>
                      <InterviewStandingBadge interview={interview} />
                      <CandidateRestrictionBadge row={interview} />
                    </>
                  }
                  subtitle={
                    <span className="text-ink-muted">
                      Interview for{' '}
                      <span className="text-ink font-semibold">{interview.internship_title}</span>
                      {interview.title && <> · {interview.title}</>}
                    </span>
                  }
                  facts={[
                    { icon: 'tag', text: `Round ${interview.round_number}` },
                    { icon: 'calendarClock', text: interviewWhen(interview) },
                    {
                      icon: interview.mode === 'ONSITE' ? 'home' : 'chat',
                      // A meeting link is one long word; let it break rather
                      // than push the chip out of the box on a phone.
                      text: (
                        <span className="min-w-0 wrap-anywhere">
                          {MODE_LABELS[interview.mode]} · {whereToGo(interview)}
                        </span>
                      ),
                    },
                    interview.feedback_count > 0 && {
                      icon: 'star',
                      text: `${interview.feedback_count} feedback · ${interview.average_rating}/5`,
                      tone: 'brand',
                    },
                    interview.reschedule_count > 0 && {
                      icon: 'repeat',
                      text: `Moved ${interview.reschedule_count} ${
                        interview.reschedule_count === 1 ? 'time' : 'times'
                      }`,
                    },
                  ]}
                  description={
                    interview.cancellation_reason && `Cancelled: ${interview.cancellation_reason}`
                  }
                  meta={
                    <span className="text-ink-muted text-xs">
                      Booked {formatDate(interview.created_at)}
                    </span>
                  }
                  actions={
                    <RoundActions
                      interview={interview}
                      blocked={blocked}
                      onMove={setMoving}
                      onRecord={setClosing}
                      onAccept={(round) => answer.accept(round)}
                      onDecline={(round) => answer.decline(round)}
                    />
                  }
                  // Cancelling ends the round, so it sits apart in the corner.
                  corner={
                    interview.status === 'SCHEDULED' && (
                      <BlockedAction reason={blocked}>
                        <Button
                          variant="ghostDanger"
                          size="sm"
                          title={`Cancel the interview with ${name}`}
                          disabled={Boolean(blocked)}
                          onClick={() => setCancelling(interview)}
                        >
                          <Icon name="close" size={14} />
                          Cancel interview
                        </Button>
                      </BlockedAction>
                    )
                  }
                >
                  <InterviewStandingNote interview={interview} />
                  {hasOpenRequest(interview) && (
                    <RescheduleRequestPanel
                      request={interview.reschedule_request}
                      candidateName={name}
                      disabledReason={blocked}
                      onUseSlot={(slot) => answer.accept(interview, slot)}
                    />
                  )}
                  {interview.student_response === 'ACCEPTED' && interview.student_message && (
                    <p className="text-ink-muted mt-2 text-sm italic">
                      “{interview.student_message}”
                    </p>
                  )}
                </BoxRow>
              );
            })}
          </BoxList>
        </ListCard>
      )}

      <InterviewSummary
        interview={selected}
        onClose={() => setSelected(null)}
        onRecord={(interview) => {
          setSelected(null);
          setClosing(interview);
        }}
        onMove={(interview) => {
          setSelected(null);
          setMoving(interview);
        }}
        onCancel={(interview) => {
          setSelected(null);
          setCancelling(interview);
        }}
        onAnswer={{
          accept: (interview, slot) => {
            setSelected(null);
            answer.accept(interview, slot);
          },
          decline: (interview) => {
            setSelected(null);
            answer.decline(interview);
          },
        }}
      />

      {moving && <ScheduleDialog open onClose={() => setMoving(null)} interview={moving} />}

      {answer.dialogs}

      {closing && <CloseDialog interview={closing} onClose={() => setClosing(null)} />}

      <Modal
        open={Boolean(cancelling)}
        title={`Cancel "${cancelling?.title}"?`}
        description="The candidate sees the reason you give."
        onClose={() => setCancelling(null)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            required
            placeholder="The role has been put on hold."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCancelling(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={cancel.isPending}
              onClick={() => cancel.mutate({ id: cancelling.id, reason: reason.trim() })}
            >
              Cancel interview
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** What the calendar opens: the round at a glance, with the list's actions. */
function InterviewSummary({ interview, onClose, onRecord, onMove, onCancel, onAnswer }) {
  if (!interview) return null;
  const name = interview.candidate.full_name;
  const scheduled = interview.status === 'SCHEDULED';
  const blocked = blockedActionReason(interview);

  return (
    <Modal open title={name} description={interview.internship_title} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge dot tone={INTERVIEW_TONES[interview.status] ?? 'neutral'}>
            {INTERVIEW_LABELS[interview.status] ?? interview.status}
          </Badge>
          <InterviewStandingBadge interview={interview} />
          <CandidateRestrictionBadge row={interview} />
        </div>
        {blocked && <p className="text-danger-700 text-sm">{blocked}</p>}
        <InterviewStandingNote interview={interview} className="mt-0" />
        {hasOpenRequest(interview) && (
          <RescheduleRequestPanel
            className="mt-0"
            request={interview.reschedule_request}
            candidateName={name}
            disabledReason={blocked}
            onUseSlot={(slot) => onAnswer.accept(interview, slot)}
            actions={
              <>
                <Button
                  size="sm"
                  variant="tintDanger"
                  disabled={Boolean(blocked)}
                  onClick={() => onAnswer.decline(interview)}
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  variant="tintPositive"
                  disabled={Boolean(blocked)}
                  onClick={() => onAnswer.accept(interview)}
                >
                  <Icon name="check" size={14} />
                  Accept
                </Button>
              </>
            }
          />
        )}

        <dl className="space-y-2 text-sm">
          <SummaryRow label="Round">
            {interview.round_number} · {interview.title}
          </SummaryRow>
          <SummaryRow label="When">{interviewWhen(interview)}</SummaryRow>
          <SummaryRow label={MODE_LABELS[interview.mode]}>
            <span className="wrap-anywhere">{whereToGo(interview)}</span>
          </SummaryRow>
          {interview.cancellation_reason && (
            <SummaryRow label="Cancelled">{interview.cancellation_reason}</SummaryRow>
          )}
        </dl>

        <div className="flex flex-wrap justify-end gap-2">
          {scheduled && (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={Boolean(blocked)}
                title={blocked ?? undefined}
                onClick={() => onCancel(interview)}
              >
                Cancel interview
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={Boolean(blocked)}
                title={blocked ?? undefined}
                onClick={() => onMove(interview)}
              >
                <Icon name="calendar" size={15} />
                Change
              </Button>
            </>
          )}
          <LinkButton
            size="sm"
            variant="secondary"
            to={paths.company.applicationDetail(interview.application_id)}
          >
            <Icon name="eye" size={15} />
            View application
          </LinkButton>
          {(canRecordOutcome(interview) || interview.status === 'COMPLETED') && (
            <Button
              size="sm"
              variant={scheduled ? 'success' : 'secondary'}
              // Feedback on a past round is the team's own; recording the
              // outcome of a booked one moves the application.
              disabled={scheduled && Boolean(blocked)}
              title={(scheduled && blocked) || undefined}
              onClick={() => onRecord(interview)}
            >
              <Icon name={scheduled ? 'check' : 'star'} size={15} />
              {scheduled ? 'Record outcome' : 'Feedback'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex gap-3">
      <dt className="text-ink-muted w-24 shrink-0">{label}</dt>
      <dd className="text-ink min-w-0">{children}</dd>
    </div>
  );
}

/**
 * The footer of a round, by where it stands (`interviewStanding`): the
 * candidate's request gets Decline / Accept, "can't make it" gets a new
 * time, a confirmed round its call and its outcome (never before the
 * candidate accepts).
 * Cancelling sits apart in the card's corner.
 */
function RoundActions({ interview, blocked, onMove, onRecord, onAccept, onDecline }) {
  const name = interview.candidate.full_name;
  const viewApplication = (
    <ViewButton
      to={paths.company.applicationDetail(interview.application_id)}
      title={`Open ${name}'s application`}
      label="View application"
      icon="fileText"
    />
  );

  if (interview.status === 'COMPLETED') {
    return (
      <CardActions>
        <Button
          variant="secondary"
          size="sm"
          title={`Feedback on the interview with ${name}`}
          onClick={() => onRecord(interview)}
        >
          <Icon name="star" size={14} />
          Feedback
        </Button>
        {viewApplication}
      </CardActions>
    );
  }
  if (interview.status !== 'SCHEDULED') return <CardActions>{viewApplication}</CardActions>;

  const standing = interviewStanding(interview);
  const guarded = (button) => <BlockedAction reason={blocked}>{button}</BlockedAction>;
  const reschedule = (variant = 'secondary') =>
    guarded(
      <Button
        variant={variant}
        size="sm"
        title={`Choose a new time for the interview with ${name}`}
        disabled={Boolean(blocked)}
        onClick={() => onMove(interview)}
      >
        <Icon name="calendar" size={14} />
        Reschedule
      </Button>,
    );
  const record = (variant = 'secondary') =>
    guarded(
      <Button
        variant={variant}
        size="sm"
        title={`Record the outcome of the interview with ${name}`}
        disabled={Boolean(blocked)}
        onClick={() => onRecord(interview)}
      >
        <Icon name="check" size={14} />
        Record outcome
      </Button>,
    );

  if (standing.key === 'requested') {
    return (
      <CardActions>
        <ActionMenu label={`More actions for the interview with ${name}`}>
          <ActionMenuItem
            icon="calendar"
            disabled={Boolean(blocked)}
            reason={blocked}
            onSelect={() => onMove(interview)}
          >
            Reschedule to another time
          </ActionMenuItem>
          <ActionMenuItem
            icon="fileText"
            to={paths.company.applicationDetail(interview.application_id)}
          >
            View application
          </ActionMenuItem>
        </ActionMenu>
        {guarded(
          <Button
            variant="tintDanger"
            size="sm"
            title="Declining cancels this interview"
            disabled={Boolean(blocked)}
            onClick={() => onDecline(interview)}
          >
            <Icon name="close" size={14} />
            Decline
          </Button>,
        )}
        {guarded(
          <Button
            variant="tintPositive"
            size="sm"
            title={`Move the interview to a time ${name} asked for`}
            disabled={Boolean(blocked)}
            onClick={() => onAccept(interview)}
          >
            <Icon name="check" size={14} />
            Accept
          </Button>,
        )}
      </CardActions>
    );
  }

  if (standing.started && canRecordOutcome(interview)) {
    // The slot has begun and the candidate accepted: the outcome is next.
    return (
      <CardActions>
        {viewApplication}
        {record('tintPositive')}
      </CardActions>
    );
  }

  if (standing.key === 'declined') {
    return (
      <CardActions>
        {viewApplication}
        {reschedule('tintBrand')}
      </CardActions>
    );
  }

  const joinUrl =
    standing.key === 'confirmed' && interview.mode === 'VIDEO' ? interview.meeting_url : null;
  return (
    <CardActions>
      {reschedule()}
      {viewApplication}
      {/* Only once the candidate has accepted; the API refuses it before. */}
      {canRecordOutcome(interview) && record()}
      {joinUrl && (
        <a href={joinUrl} target="_blank" rel="noreferrer noopener" className={JOIN_CALL_CLASS}>
          <Icon name="external" size={14} />
          Join the call
        </a>
      )}
    </CardActions>
  );
}
